import { useState } from "react";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useGet } from "../lib/hooks";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";

function ProfileEditor({ meta, initial, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", description: "", permissions: {}, ...initial });
  const toggle = (a, p) => {
    const cur = f.permissions[a] || [];
    let next = cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p];
    if (p !== "view" && next.includes(p) && !next.includes("view")) next = ["view", ...next];
    if (p === "view" && !next.includes("view")) next = [];
    setF({ ...f, permissions: { ...f.permissions, [a]: next } });
  };
  const save = async () => {
    if (!f.name.trim()) return toast.error("Profile name is required");
    try { f.id ? await api.put(`/access-profiles/${f.id}`, f) : await api.post("/access-profiles", f); toast.success("Access profile saved"); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-popover" data-testid="access-profile-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{f.id ? "Edit access profile" : "New access profile"}</DialogTitle>
          <DialogDescription className="text-xs">Choose what people with this profile can see and do. Ticking create, edit or delete also turns on view.</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input placeholder="Profile name (e.g. Support Worker)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="access-profile-name" />
          <Input placeholder="Description (optional)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="access-profile-description" />
        </div>
        <table className="w-full text-sm mt-2">
          <thead><tr className="border-b"><th className="text-left p-2 overline">Area</th>{meta.perms.map((p) => <th key={p} className="p-2 overline">{p}</th>)}</tr></thead>
          <tbody>{meta.areas.map((a) => (
            <tr key={a.key} className="border-b border-white/5">
              <td className="p-2">{a.label}</td>
              {meta.perms.map((p) => <td key={p} className="p-2 text-center"><Checkbox checked={(f.permissions[a.key] || []).includes(p)} onCheckedChange={() => toggle(a.key, p)} data-testid={`ap-${a.key}-${p}`} /></td>)}
            </tr>))}
          </tbody>
        </table>
        <DialogFooter><Button variant="ghost" onClick={onClose} data-testid="access-profile-cancel">Cancel</Button><Button onClick={save} data-testid="access-profile-save">Save profile</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AccessProfilesTab() {
  const [profiles, reload] = useGet("/access-profiles");
  const [meta] = useGet("/access-areas");
  const [edit, setEdit] = useState(null);
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? People using it will fall back to the default staff access.`)) return;
    try { await api.delete(`/access-profiles/${p.id}`); toast.success("Deleted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const label = (k) => meta?.areas.find((a) => a.key === k)?.label || k;
  return (
    <div className="space-y-3" data-testid="access-profiles-tab">
      <p className="text-sm text-muted-foreground max-w-3xl">Access profiles control what Staff can see and do. Owners and Entity Admins always have full access. Staff with no profile use the default staff access set in Record Centre → User Role Matrix. Modules switched off for this entity are hidden from everyone.</p>
      <Button onClick={() => setEdit({})} disabled={!meta} data-testid="new-access-profile-btn"><Plus className="h-4 w-4 mr-1" />New access profile</Button>
      {(profiles || []).map((p) => {
        const areas = Object.entries(p.permissions || {}).filter(([, v]) => v.length);
        return (
          <div key={p.id} className="rounded-md border bg-card p-4 flex items-start gap-4" data-testid={`access-profile-${p.id}`}>
            <ShieldCheck className="h-5 w-5 text-[#C2A462] mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">{p.name} <span className="text-xs font-normal text-muted-foreground">· {p.users} {p.users === 1 ? "person" : "people"}{p.group_count > 0 && <span data-testid={`profile-via-groups-${p.id}`}> · +{p.via_groups} staff via {p.group_count === 1 ? "a group" : `${p.group_count} groups`}</span>}</span></div>
              {p.description && <div className="text-xs text-muted-foreground">{p.description}</div>}
              <div className="text-xs text-muted-foreground mt-1">{areas.length ? areas.map(([k, v]) => `${label(k)} (${v.join(", ")})`).join(" · ") : "No access to any area"}</div>
            </div>
            <Button size="icon" variant="ghost" onClick={() => setEdit(p)} data-testid={`edit-access-profile-${p.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => remove(p)} data-testid={`delete-access-profile-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
          </div>
        );
      })}
      {profiles && !profiles.length && <div className="text-sm text-muted-foreground">No access profiles yet.</div>}
      {edit && meta && <ProfileEditor meta={meta} initial={edit} onClose={() => setEdit(null)} onSaved={reload} />}
    </div>
  );
}
